/**
 * WHICH PAGE OF A BUSINESS AN ADDRESS IS — shared by the journey (shopJourney.js) and the movement
 * recorder (shopMovement.js), which must not import each other in a circle.
 */
import { isDomainMode } from './domainMode';
import { variantIdInLink } from './variantLink';

/* ── Which page ───────────────────────────────────────────────────────── */

/** A business's pages, by their path under the business (its slug on stylon.online, the root on its own domain). */
const PAGES = [
  [/^p\/[^/]+$/, 'product'],
  [/^shop$/, 'shop_home'],
  [/^shop\/c\/[^/]+$/, 'category'],
  [/^shop\/brands\/[^/]+$/, 'brand'],
  [/^shop\/collections(?:\/[^/]+)?$/, 'collection'],
  [/^checkout$/, 'checkout'],
];
/**
 * The rest of a business's own pages on its domain, where the account pages (a profile, an order,
 * a payment link with its token) sit beside them at the root and are nobody's journey. On
 * stylon.online everything under the business's slug is its own.
 */
const OTHER_DOMAIN_PAGES = [/^$/, /^order-placed$/, /^shop\/policies\/[^/]+$/, /^stays$/, /^stay\/[^/]+$/, /^courses(?:\/[^/]+)?$/];

/**
 * `{ pageType, productId, variantId }` for an address, or null when it is not a business's page.
 * A shop page carrying a search (`?q=`) is a search; its words are never read.
 */
export function journeyPageOf(pathname, search, domainMode = isDomainMode()) {
  const parts = String(pathname || '').split('/').filter(Boolean);
  if (!domainMode && parts.length === 0) return null;
  const rest = (domainMode ? parts : parts.slice(1)).join('/');
  const found = PAGES.find(([re]) => re.test(rest));
  if (!found && domainMode && !OTHER_DOMAIN_PAGES.some((re) => re.test(rest))) return null;
  let pageType = found ? found[1] : 'other';
  let hasQuery = false;
  try { hasQuery = !!new URLSearchParams(search || '').get('q'); } catch (_) { /* no query */ }
  if (pageType === 'shop_home' && hasQuery) pageType = 'search';
  const productId = pageType === 'product' ? decodeSegment(rest.split('/')[1]) : null;
  const variantId = pageType === 'product' ? variantIdInLink(search) : null;
  return { pageType, productId, variantId };
}

function decodeSegment(segment) {
  try { return decodeURIComponent(segment); } catch (_) { return segment; }
}
